import type { Material } from '../types';
import * as supplyApi from '../api/supply';
import { KeyedAsyncCache } from './asyncCache';

const ORIGIN_FROM_BACKEND: Record<supplyApi.BackendMaterialOrigin, NonNullable<Material['nguonGoc']>> = {
  HTX_ISSUED: 'HTX cấp/ứng',
  SELF_PURCHASED: 'Nông hộ tự mua',
};

const STATUS_FROM_BACKEND: Record<supplyApi.BackendMaterialStatus, Material['status']> = {
  PENDING_ISSUE: 'Chờ cấp',
  ISSUED: 'Đã cấp',
  IN_USE: 'Đang sử dụng',
  RETURNED: 'Đã trả',
  DEPLETED: 'Đã hết',
};

const STATUS_TO_BACKEND: Partial<Record<Material['status'], supplyApi.BackendMaterialStatus>> = {
  'Chờ cấp': 'PENDING_ISSUE',
  'Đã cấp': 'ISSUED',
  'Đang sử dụng': 'IN_USE',
  'Đã trả': 'RETURNED',
  'Đã hết': 'DEPLETED',
};

function toFrontendMaterial(m: supplyApi.BackendMaterial): Material {
  return {
    id: m._id,
    farmerId: m.farmerId,
    seasonId: m.seasonId,
    maVatTu: m.code,
    tenVatTu: m.name,
    nhomVatTu: m.group,
    soLuong: m.quantity,
    tonKho: m.remainingQuantity ?? m.quantity,
    donVi: m.unit,
    nguonGoc: ORIGIN_FROM_BACKEND[m.origin],
    ngayCap: m.issuedDate,
    ngayTra: m.returnedDate,
    ghiChu: m.notes,
    status: STATUS_FROM_BACKEND[m.status] ?? 'Chờ cấp',
    attachments: m.attachments,
  };
}

const byFarmer = new KeyedAsyncCache<Material[]>(
  async (farmerId) => (await supplyApi.listMaterialsByFarmer(farmerId)).map(toFrontendMaterial),
  [],
  'materials:byFarmer',
);

export function getMaterialsByFarmer(farmerId?: string): Material[] {
  if (!farmerId) return [];
  return byFarmer.get(farmerId);
}

function refresh(farmerId: string) {
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
}

/** Only self-purchased material creation exists on the real backend —
 * HTX-issued material comes from `issueMaterialToFarmer` (the Supply domain,
 * see `store/supply.ts`) instead of this farmer-facing "add material" flow. */
export async function addMaterial(data: Omit<Material, 'id'>): Promise<Material> {
  const created = await supplyApi.createSelfPurchasedMaterial({
    farmerId: data.farmerId,
    seasonId: data.seasonId,
    name: data.tenVatTu,
    group: data.nhomVatTu,
    quantity: data.soLuong,
    unit: data.donVi,
    notes: data.ghiChu,
    attachments: data.attachments,
  });
  refresh(data.farmerId);
  return toFrontendMaterial(created);
}

/** The backend exposes a status transition only (`PATCH /materials/:id/status`)
 * — quantity/name/unit stay immutable after creation. To correct those, delete
 * the entry (see `deleteMaterial`) and log it again. */
export async function updateMaterialStatus(id: string, status: Material['status'], farmerId: string): Promise<Material> {
  const backendStatus = STATUS_TO_BACKEND[status];
  if (!backendStatus) throw new Error(`Trạng thái "${status}" không có trên hệ thống thật`);
  const updated = await supplyApi.updateMaterialStatus(id, backendStatus);
  refresh(farmerId);
  return toFrontendMaterial(updated);
}

/** Removes a self-purchased entry the farmer logged by mistake. HTX-issued
 * material is refused by the backend — it mirrors a stock-out already written to
 * the HTX's supply ledger, so only the HTX can unwind it. */
export async function deleteMaterial(id: string, farmerId: string): Promise<void> {
  await supplyApi.deleteMaterial(id);
  refresh(farmerId);
}
